import { makeAutoObservable } from "mobx";
import firebase from "firebase/compat/app";
import workspaces from "./workspaces";
import firebaseApp from "firebase/compat/app";
import { Article, Collection, FAQ } from "../models/HelpCenter";
import { transferEmbedding } from "../services/chathub";
import { EmbeddingStatus } from "../models/HelpCenter";

class HelpCenterStore {
  private db: firebase.firestore.Firestore;

  constructor() {
    makeAutoObservable(this);
    this.db = firebase.firestore();
  }

  transferFAQs = async (faqIds: string[], targetWorkspaceId: string) => {
    if (!workspaces.selectedWorkspace) {
      throw new Error("No workspace selected");
    }

    const sourceWorkspaceId = workspaces.selectedWorkspace.id;

    if (sourceWorkspaceId === targetWorkspaceId) {
      throw new Error("Cannot transfer to the same workspace");
    }

    try {
      // Get all FAQs to transfer
      const faqsSnapshot = await this.db
        .collection("workspaces")
        .doc(sourceWorkspaceId)
        .collection("faqs")
        .where(firebase.firestore.FieldPath.documentId(), "in", faqIds)
        .get();

      const faqsToTransfer = faqsSnapshot.docs.map((doc) => {
        const faq = doc.data() as FAQ;
        faq.id = doc.id;
        return faq;
      });

      if (faqsToTransfer.length === 0) {
        throw new Error("No FAQs found to transfer");
      }

      // Prepare embedding transfer data
      const embeddingTransferData = faqsToTransfer.map((faq) => ({
        sourceCollectionId: faq.id!,
        sourceCollectionName: "faq",
        targetCollectionId: faq.id!,
        targetCollectionName: "faq",
        text: `Question: ${faq.question}\nAnswer: ${faq.answer}`,
        meta: {
          question: faq.question,
          answer: faq.answer,
        },
      }));

      // Transfer embeddings in the vector database
      await transferEmbedding(
        sourceWorkspaceId,
        targetWorkspaceId,
        embeddingTransferData
      );

      // Create FAQs in target workspace using batch
      const batch = this.db.batch();

      for (const faq of faqsToTransfer) {
        const newFAQRef = this.db
          .collection("workspaces")
          .doc(targetWorkspaceId)
          .collection("faqs")
          .doc(faq.id);

        batch.set(newFAQRef, {
          question: faq.question,
          answer: faq.answer,
          embeddingStatus: EmbeddingStatus.TRAINED,
          lastEmbeddidAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });
      }

      await batch.commit();

      // Delete FAQs from source workspace
      const deleteBatch = this.db.batch();

      for (const faq of faqsToTransfer) {
        const sourceFAQRef = this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("faqs")
          .doc(faq.id!);

        deleteBatch.delete(sourceFAQRef);
      }

      await deleteBatch.commit();

      return {
        success: true,
        transferred: faqsToTransfer.length,
      };
    } catch (error) {
      console.error("Error transferring FAQs:", error);
      throw error;
    }
  };

  transferCollections = async (
    collectionIds: string[],
    targetWorkspaceId: string
  ) => {
    if (!workspaces.selectedWorkspace) {
      throw new Error("No workspace selected");
    }

    const sourceWorkspaceId = workspaces.selectedWorkspace.id;

    if (sourceWorkspaceId === targetWorkspaceId) {
      throw new Error("Cannot transfer to the same workspace");
    }

    try {
      // Get all collections to transfer
      const collectionsSnapshot = await this.db
        .collection("workspaces")
        .doc(sourceWorkspaceId)
        .collection("collections")
        .where(firebase.firestore.FieldPath.documentId(), "in", collectionIds)
        .get();

      const collectionsToTransfer = collectionsSnapshot.docs.map((doc) => {
        const collection = doc.data() as Collection;
        collection.id = doc.id;
        return collection;
      });

      if (collectionsToTransfer.length === 0) {
        throw new Error("No collections found to transfer");
      }

      const embeddingTransferData = [];
      const articlesToTransfer: Article[] = [];

      // For each collection, get its articles
      for (const collection of collectionsToTransfer) {
        // Add collection embedding
        embeddingTransferData.push({
          sourceCollectionId: collection.id!,
          sourceCollectionName: "collection",
          targetCollectionId: collection.id!,
          targetCollectionName: "collection",
          text: collection.name,
          meta: { name: collection.name },
        });

        // Get all articles in this collection
        const articlesSnapshot = await this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("articles")
          .where("collectionId", "==", collection.id)
          .get();

        const articles = articlesSnapshot.docs.map((doc) => {
          const article = doc.data() as Article;
          article.id = doc.id;
          return article;
        });

        articlesToTransfer.push(...articles);

        // Add article embeddings
        for (const article of articles) {
          embeddingTransferData.push({
            sourceCollectionId: article.id!,
            sourceCollectionName: "article",
            targetCollectionId: article.id!,
            targetCollectionName: "article",
            text: `Title: ${article.title}\nDescription: ${article.description}`,
            meta: {
              title: article.title,
              description: article.description,
              collectionName: article.collectionName,
            },
          });
        }
      }

      // Transfer all embeddings (collections + articles)
      await transferEmbedding(
        sourceWorkspaceId,
        targetWorkspaceId,
        embeddingTransferData
      );

      // Create collections in target workspace
      const batch = this.db.batch();

      for (const collection of collectionsToTransfer) {
        const newCollectionRef = this.db
          .collection("workspaces")
          .doc(targetWorkspaceId)
          .collection("collections")
          .doc(collection.id);

        batch.set(newCollectionRef, {
          name: collection.name,
          noOfArticles: collection.noOfArticles,
          embeddingStatus: EmbeddingStatus.TRAINED,
          lastEmbeddidAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });
      }

      // Create articles in target workspace
      for (const article of articlesToTransfer) {
        const newArticleRef = this.db
          .collection("workspaces")
          .doc(targetWorkspaceId)
          .collection("articles")
          .doc(article.id);

        batch.set(newArticleRef, {
          userId: article.userId,
          workspaceId: targetWorkspaceId,
          collectionId: article.collectionId,
          collectionName: article.collectionName,
          title: article.title,
          description: article.description,
          status: article.status,
          lang: article.lang,
          embeddingStatus: EmbeddingStatus.TRAINED,
          lastEmbeddidAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });
      }

      await batch.commit();

      // Delete articles from source workspace
      const deleteArticlesBatch = this.db.batch();

      for (const article of articlesToTransfer) {
        const sourceArticleRef = this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("articles")
          .doc(article.id!);

        deleteArticlesBatch.delete(sourceArticleRef);
      }

      await deleteArticlesBatch.commit();

      // Delete collections from source workspace
      const deleteCollectionsBatch = this.db.batch();

      for (const collection of collectionsToTransfer) {
        const sourceCollectionRef = this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("collections")
          .doc(collection.id!);

        deleteCollectionsBatch.delete(sourceCollectionRef);
      }

      await deleteCollectionsBatch.commit();

      return {
        success: true,
        transferred: collectionsToTransfer.length,
        articlesTransferred: articlesToTransfer.length,
      };
    } catch (error) {
      console.error("Error transferring collections:", error);
      throw error;
    }
  };

  transferArticles = async (
    articleIds: string[],
    targetWorkspaceId: string,
    targetCollectionId: string
  ) => {
    if (!workspaces.selectedWorkspace) {
      throw new Error("No workspace selected");
    }

    const sourceWorkspaceId = workspaces.selectedWorkspace.id;

    if (sourceWorkspaceId === targetWorkspaceId) {
      throw new Error("Cannot transfer to the same workspace");
    }

    try {
      // Get target collection info
      const targetCollectionDoc = await this.db
        .collection("workspaces")
        .doc(targetWorkspaceId)
        .collection("collections")
        .doc(targetCollectionId)
        .get();

      if (!targetCollectionDoc.exists) {
        throw new Error("Target collection not found");
      }

      const targetCollection = targetCollectionDoc.data() as Collection;
      targetCollection.id = targetCollectionDoc.id;

      // Get all articles to transfer
      const articlesSnapshot = await this.db
        .collection("workspaces")
        .doc(sourceWorkspaceId)
        .collection("articles")
        .where(firebase.firestore.FieldPath.documentId(), "in", articleIds)
        .get();

      const articlesToTransfer = articlesSnapshot.docs.map((doc) => {
        const article = doc.data() as Article;
        article.id = doc.id;
        return article;
      });

      if (articlesToTransfer.length === 0) {
        throw new Error("No articles found to transfer");
      }

      // Prepare embedding transfer data
      const embeddingTransferData = articlesToTransfer.map((article) => ({
        sourceCollectionId: article.id!,
        sourceCollectionName: "article",
        targetCollectionId: article.id!,
        targetCollectionName: "article",
        text: `Title: ${article.title}\nDescription: ${article.description}`,
        meta: {
          title: article.title,
          description: article.description,
          collectionName: targetCollection.name,
        },
      }));

      // Transfer embeddings in the vector database
      await transferEmbedding(
        sourceWorkspaceId,
        targetWorkspaceId,
        embeddingTransferData
      );

      // Create articles in target workspace
      const batch = this.db.batch();

      for (const article of articlesToTransfer) {
        const newArticleRef = this.db
          .collection("workspaces")
          .doc(targetWorkspaceId)
          .collection("articles")
          .doc(article.id);

        batch.set(newArticleRef, {
          userId: article.userId,
          workspaceId: targetWorkspaceId,
          collectionId: targetCollectionId,
          collectionName: targetCollection.name,
          title: article.title,
          description: article.description,
          status: article.status,
          lang: article.lang,
          embeddingStatus: EmbeddingStatus.TRAINED,
          lastEmbeddidAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          createdAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });
      }

      await batch.commit();

      // Update target collection article count
      await this.db
        .collection("workspaces")
        .doc(targetWorkspaceId)
        .collection("collections")
        .doc(targetCollectionId)
        .update({
          noOfArticles:
            firebaseApp.firestore.FieldValue.increment(
              articlesToTransfer.length
            ),
          updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
        });

      // Update source collections article counts and delete articles
      const sourceCollectionUpdates = new Map<string, number>();

      for (const article of articlesToTransfer) {
        const count = sourceCollectionUpdates.get(article.collectionId) || 0;
        sourceCollectionUpdates.set(article.collectionId, count + 1);
      }

      // Delete articles from source workspace
      const deleteBatch = this.db.batch();

      for (const article of articlesToTransfer) {
        const sourceArticleRef = this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("articles")
          .doc(article.id!);

        deleteBatch.delete(sourceArticleRef);
      }

      await deleteBatch.commit();

      // Update source collection counts
      for (const [collectionId, count] of sourceCollectionUpdates) {
        await this.db
          .collection("workspaces")
          .doc(sourceWorkspaceId)
          .collection("collections")
          .doc(collectionId)
          .update({
            noOfArticles: firebaseApp.firestore.FieldValue.increment(-count),
            updatedAt: firebaseApp.firestore.FieldValue.serverTimestamp(),
          });
      }

      return {
        success: true,
        transferred: articlesToTransfer.length,
      };
    } catch (error) {
      console.error("Error transferring articles:", error);
      throw error;
    }
  };
}

export default new HelpCenterStore();
